import { useCallback, useEffect, useRef, useState } from "react";
import type { Job, JobState, Driver } from "@eagledispatch/shared";
import { apiFetch } from "../../lib/api";
import Button from "../../components/Button";
import Card from "../../components/Card";
import StatusTag from "../../components/StatusTag";
import type { DispatcherRoute } from "./DispatchHome";

const TABS: { key: JobState | "ALL"; label: string }[] = [
  { key: "NEW", label: "New" },
  { key: "IN_PROGRESS", label: "In Progress" },
  { key: "PAUSED", label: "Paused" },
  { key: "FINISHED", label: "Finished" },
  { key: "REVIEWED", label: "Reviewed" },
  { key: "ALL", label: "All" },
];

const PAGE_SIZE = 25;

interface JobListResponse {
  jobs: Job[];
  total: number;
}

export default function JobList({
  initialTab = "NEW",
  onNavigate,
}: {
  initialTab?: JobState | "ALL";
  onNavigate: (route: DispatcherRoute) => void;
}) {
  const [tab, setTab] = useState<JobState | "ALL">(initialTab);
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [driverNames, setDriverNames] = useState<Map<string, string>>(new Map());

  // Monotonic load sequence. Every load (initial, poll, focus, pagination, tab
  // switch) stamps itself with an incrementing id; a response is applied only
  // if its id is still the latest. This prevents an older in-flight response
  // (e.g. a slow poll) from clobbering a newer one (a fast focus refetch).
  const loadSeqRef = useRef(0);

  // The jobs list returns flat rows (no nested driver), so fetch the driver
  // roster once to resolve driverId → name for each row.
  useEffect(() => {
    apiFetch<Driver[]>("/api/v1/drivers")
      .then((drivers) => {
        const map = new Map<string, string>();
        for (const d of drivers) map.set(d.id, d.name);
        setDriverNames(map);
      })
      .catch(() => {
        // Non-fatal: rows just render "—" for driver name.
      });
  }, []);

  const load = useCallback(async () => {
    const stateParam = tab === "ALL" ? undefined : tab;
    const query =
      stateParam !== undefined ? `&state=${encodeURIComponent(stateParam)}` : "";
    const seq = ++loadSeqRef.current;
    try {
      const res = await apiFetch<JobListResponse>(
        `/api/v1/jobs?limit=${PAGE_SIZE}&offset=${offset}${query}`,
      );
      if (seq !== loadSeqRef.current) return; // superseded by a newer load
      setJobs(res.jobs);
      setTotal(res.total);
      setError(null);
    } catch (e) {
      if (seq !== loadSeqRef.current) return; // superseded — ignore stale error
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [tab, offset]);

  // Reset offset when the tab changes, then load.
  useEffect(() => {
    setOffset(0);
    setJobs(null);
  }, [tab]);

  useEffect(() => {
    void load();
  }, [load]);

  // 30-second polling + refetch on window focus.
  useEffect(() => {
    const id = window.setInterval(() => void load(), 30_000);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const customerName = (job: Job): string => {
    const snap = (job.customerSnapshot ?? {}) as { customer?: { companyName?: string } };
    return snap?.customer?.companyName ?? "—";
  };

  const driverName = (job: Job): string =>
    (job.driverId && driverNames.get(job.driverId)) || "—";

  const hasMore = offset + (jobs?.length ?? 0) < total;

  return (
    <main className="surface">
      <header className="surface-header">
        <Button variant="ghost" onClick={() => onNavigate({ name: "home" })}>
          ← Back
        </Button>
        <h1>Jobs</h1>
      </header>

      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`tab ${tab === t.key ? "tab--active" : ""}`.trim()}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && jobs === null ? (
        <Card>
          <p className="err">Failed to load jobs.</p>
          <p className="muted">{error}</p>
          <Button onClick={() => void load()}>Retry</Button>
        </Card>
      ) : jobs === null ? (
        <p className="muted">Loading…</p>
      ) : jobs.length === 0 ? (
        <Card>
          <p className="muted">No jobs in this state.</p>
        </Card>
      ) : (
        <ul className="job-list">
          {jobs.map((job) => (
            <li key={job.id}>
              <button
                className="job-row"
                onClick={() => onNavigate({ name: "job", id: job.id })}
              >
                <div className="job-row-top">
                  <span className="job-number">{job.jobNumber}</span>
                  <StatusTag state={job.state} />
                </div>
                <div className="job-row-meta">
                  <span>{customerName(job)}</span>
                  <span className="muted">·</span>
                  <span>{driverName(job)}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && jobs !== null && (
        <Card className="list-banner">
          <p className="muted">Couldn't refresh — showing last update.</p>
          <Button variant="ghost" onClick={() => void load()}>
            Retry
          </Button>
        </Card>
      )}

      {jobs !== null && jobs.length > 0 && (
        <div className="pagination">
          <Button
            variant="ghost"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            ← Prev
          </Button>
          <span className="muted">
            {offset + 1}–{offset + jobs.length} of {total}
          </span>
          <Button
            variant="ghost"
            disabled={!hasMore}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next →
          </Button>
        </div>
      )}
    </main>
  );
}
